import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import { createSession } from '../services/sessionService';
import { cancelSession, deleteSession, updateSessionStatus } from '../services/sessionManagementService';
import { SeasonProvider } from '../context/SeasonContext';
import Button from '../components/common/Button';
import CustomSessionConfigModal from '../components/session/CustomSessionConfigModal';
import SessionNavigationButton from '../components/common/SessionNavigationButton';
import CancelDeleteModal from '../components/sessions/CancelDeleteModal';
import { useClubPermissions } from '../hooks/useClubPermissions';
import { Heading, Text, Typography } from '../components/common/Typography';
import { Card } from '../components/common/Card';
import { BackButton } from '../components/common';
import { navigateBack } from '../utils/navigationUtils';
import { formatSessionTimeDisplay } from '../utils/homeSessionUtils';

const formatSessionTimeRange = (start, end) => {
  const startFmt = start ? formatSessionTimeDisplay(start) : '—';
  const endFmt = end ? formatSessionTimeDisplay(end) : '—';
  return `${startFmt} – ${endFmt}`;
};

/** Responsive row for active / passed session lists on Start Session page */
function StartSessionListItem({ session, statusBadge, actions, variant = 'default' }) {
  const bgClass = variant === 'muted' ? 'bg-surface-subtle' : 'bg-surface';

  return (
    <div className={`rounded-lg p-3 border border-border-medium ${bgClass} space-y-3 min-w-0`}>
      <div className="min-w-0 space-y-2">
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          <span className="text-sm font-medium text-text-primary min-w-0 w-full sm:w-auto sm:flex-1">
            {session.name}
          </span>
          {statusBadge}
        </div>
        <div className="text-xs text-text-secondary leading-relaxed">
          {new Date(session.session_date).toLocaleDateString()}
          {' · '}
          {formatSessionTimeRange(session.start_time, session.end_time)}
          {' · '}
          {session.current_players || 0}/{session.max_players} players
        </div>
      </div>
      {actions ? (
        <div className="flex flex-col sm:flex-row flex-wrap gap-2 w-full sm:w-auto sm:justify-end">
          {actions}
        </div>
      ) : null}
    </div>
  );
}

// Inner component that handles the actual session logic
function SessionContent({ currentUser, selectedClub, clubRole }) {
  const [showStartSessionModal, setShowStartSessionModal] = useState(false);
  const [sessionTypes, setSessionTypes] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeSessions, setActiveSessions] = useState([]);
  const [activeSessionsLoading, setActiveSessionsLoading] = useState(false);
  const [passedSessions, setPassedSessions] = useState([]);
  const [passedSessionsLoading, setPassedSessionsLoading] = useState(false);
  const [sessionForCancelDelete, setSessionForCancelDelete] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  // Check if user has session-management permissions (host, admin, owner)
  const isAdmin = currentUser?.role === 'admin' ||
                  currentUser?.role === 'super_admin' ||
                  clubRole === 'host' ||
                  clubRole === 'admin' ||
                  clubRole === 'owner';

  // Fetch session types on component mount
  useEffect(() => {
    fetchSessionTypes();
  }, []);

  // Set default selected session when session types are loaded
  useEffect(() => {
    if (sessionTypes.length > 0 && !selectedSession) {
      setSelectedSession(sessionTypes[0]);
    }
  }, [sessionTypes, selectedSession]);

  // Fetch active and passed sessions when club changes
  useEffect(() => {
    if (selectedClub?.id) {
      fetchActiveSessions(selectedClub.id);
      fetchPassedSessions(selectedClub.id);
    } else {
      setActiveSessions([]);
      setPassedSessions([]);
    }
  }, [selectedClub?.id]);

  // Auto-open create-session modal when arriving with ?start=1 (e.g. Home → pick club → create).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('start') === '1' && currentUser && selectedClub) {
      setShowStartSessionModal(true);
    }
  }, [location.search, currentUser, selectedClub]);

  const clearStartSessionQueryParam = useCallback(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('start') !== '1') return;
    params.delete('start');
    const search = params.toString();
    navigate(`${location.pathname}${search ? `?${search}` : ''}`, { replace: true });
  }, [location.pathname, location.search, navigate]);

  const handleCloseStartSessionModal = useCallback(() => {
    setShowStartSessionModal(false);
    clearStartSessionQueryParam();
  }, [clearStartSessionQueryParam]);

  // No longer handling activeSessionId from URL - sessions are now separate pages

  const fetchSessionTypes = async () => {
    try {
      const { data, error } = await supabase
        .from('session_types')
        .select('*')
        .order('name');

      if (error) throw error;
      setSessionTypes(data);
    } catch (err) {
      console.error('Error fetching session types:', err);
      // Fallback to default session type if database fetch fails
      setSessionTypes([{ id: 'default', name: 'Competitive Session' }]);
    }
  };

  // Fetch active sessions for selected club: today or future only (past sessions go in Passed Sessions)
  const fetchActiveSessions = async (clubId) => {
    if (!clubId) return;
    
    setActiveSessionsLoading(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const { data: allSessions, error: allSessionsError } = await supabase
        .from('sessions')
        .select(`
          id,
          name,
          description,
          session_date,
          start_time,
          end_time,
          session_status,
          max_players,
          current_players,
          club_id,
          created_by,
          season_id,
          session_types:session_type_id (
            id,
            name,
            description
          )
        `)
        .eq('club_id', clubId)
        .in('session_status', ['upcoming', 'ongoing'])
        .gte('session_date', today)
        .order('session_date', { ascending: true })
        .order('start_time', { ascending: true });

      if (allSessionsError) throw allSessionsError;

      console.log('All sessions fetched:', allSessions);
      setActiveSessions(allSessions || []);
    } catch (err) {
      console.error('Error fetching active sessions:', err);
      setError('Failed to fetch active sessions');
    } finally {
      setActiveSessionsLoading(false);
    }
  };

  // Fetch sessions where date has passed: never started (upcoming/draft) or started but not ended (ongoing)
  const fetchPassedSessions = async (clubId) => {
    if (!clubId) return;
    setPassedSessionsLoading(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const { data, error } = await supabase
        .from('sessions')
        .select(`
          id,
          name,
          description,
          session_date,
          start_time,
          end_time,
          session_status,
          max_players,
          current_players,
          club_id,
          session_types:session_type_id (
            id,
            name,
            description
          )
        `)
        .eq('club_id', clubId)
        .lt('session_date', today)
        .in('session_status', ['upcoming', 'draft', 'ongoing'])
        .order('session_date', { ascending: false })
        .order('start_time', { ascending: false });

      if (error) throw error;
      setPassedSessions(data || []);
    } catch (err) {
      console.error('Error fetching passed sessions:', err);
    } finally {
      setPassedSessionsLoading(false);
    }
  };

  const handleEndSession = async (sessionId) => {
    if (!selectedClub?.id || !sessionId) return;
    try {
      await updateSessionStatus(sessionId, 'completed');
      setSessionForCancelDelete(null);
      await fetchActiveSessions(selectedClub.id);
      await fetchPassedSessions(selectedClub.id);
      setError(null);
    } catch (err) {
      console.error('Error ending session:', err);
      setError(err.message || 'Failed to end session');
    }
  };

  const handleCancelOrDeleteSession = async (sessionId, action) => {
    if (!selectedClub?.id || !sessionId) return;
    try {
      if (action === 'cancel') {
        await cancelSession(sessionId);
      } else {
        await deleteSession(sessionId);
      }
      setSessionForCancelDelete(null);
      await fetchActiveSessions(selectedClub.id);
      await fetchPassedSessions(selectedClub.id);
      setError(null);
    } catch (err) {
      console.error(`Error ${action === 'cancel' ? 'cancelling' : 'deleting'} session:`, err);
      setError(err.message || `Failed to ${action === 'cancel' ? 'cancel' : 'delete'} session`);
    }
  };

  const handleSessionCreation = async (sessionData, setup) => {
    setLoading(true);
    setError(null);

    try {
      const newSession = await createSession(sessionData);
      setShowStartSessionModal(false);
      clearStartSessionQueryParam();
      navigate(`/club/${selectedClub?.id}/session/${newSession.id}`);
    } catch (err) {
      console.error('Error starting session:', err);
      setError('Failed to start session. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle starting a session - navigate to dedicated session page
  const handleStartExistingSession = async (sessionId) => {
    navigate(`/club/${selectedClub?.id}/session/${sessionId}`);
  };

  // Session Selection Interface Component
  const SessionSelectionInterface = () => (
    <Card variant="default" size="large" className="mb-6 bg-surface overflow-hidden">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between mb-4 min-w-0">
        <Heading level={2} className="text-lg font-semibold shrink-0">Start Session</Heading>
        <div className="text-sm text-text-secondary truncate">
          {selectedClub ? selectedClub.name : 'Loading club...'}
        </div>
      </div>

      {/* Active Sessions - Compact Display */}
      {selectedClub && (
        <div className="border-t border-border pt-3">
          <div className="flex items-center justify-between mb-2">
            <Heading level={3} className="text-sm font-medium text-text-primary">📅 Active Sessions</Heading>
            <span className="text-xs text-text-secondary">
              {activeSessionsLoading ? 'Loading...' : `${activeSessions.length} sessions`}
            </span>
          </div>
          
          {activeSessionsLoading ? (
            <div className="text-xs text-text-secondary text-center py-2">Loading sessions...</div>
          ) : activeSessions.length > 0 ? (
            <div className="space-y-2">
              {activeSessions.map((session) => (
                <StartSessionListItem
                  key={session.id}
                  session={session}
                  statusBadge={
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium shrink-0 ${
                      session.session_status === 'ongoing'
                        ? 'bg-success-light text-success-dark'
                        : 'bg-info-light text-info-dark'
                    }`}>
                      {session.session_status === 'ongoing' ? 'Active' : 'Upcoming'}
                    </span>
                  }
                  actions={
                    <SessionNavigationButton
                      session={session}
                      clubId={selectedClub?.id}
                      starterPlayerId={currentUser?.id}
                      action={session.session_status === 'ongoing' ? 'enter' : 'start'}
                      variant={session.session_status === 'ongoing' ? 'primary' : 'outline'}
                      size="small"
                      disabled={loading}
                      className="w-full sm:w-auto"
                    />
                  }
                />
              ))}
            </div>
          ) : (
            <div className="text-xs text-text-secondary text-center py-2">
              No sessions today for {selectedClub.name}
            </div>
          )}
        </div>
      )}

      {/* Passed Sessions - date has passed but not started / not marked cancelled */}
      {selectedClub && (
        <div className="border-t border-border pt-3 mt-3">
          <div className="flex items-center justify-between mb-2">
            <Heading level={3} className="text-sm font-medium text-text-primary">📅 Passed Sessions</Heading>
            <span className="text-xs text-text-secondary">
              {passedSessionsLoading ? 'Loading...' : `${passedSessions.length} sessions`}
            </span>
          </div>
          {passedSessionsLoading ? (
            <div className="text-xs text-text-secondary text-center py-2">Loading...</div>
          ) : passedSessions.length > 0 ? (
            <div className="space-y-2">
              {passedSessions.map((session) => {
                const isOngoing = session.session_status === 'ongoing';
                return (
                  <StartSessionListItem
                    key={session.id}
                    session={session}
                    variant="muted"
                    statusBadge={
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium shrink-0 ${
                        isOngoing
                          ? 'bg-success-light text-success-dark'
                          : session.session_status === 'draft'
                            ? 'bg-surface text-text-secondary border border-border-medium'
                            : 'bg-info-light text-info-dark'
                      }`}>
                        {isOngoing ? 'Ongoing' : session.session_status === 'draft' ? 'Draft' : 'Upcoming'}
                      </span>
                    }
                    actions={
                      isOngoing ? (
                        <>
                          <SessionNavigationButton
                            session={session}
                            clubId={selectedClub?.id}
                            action="enter"
                            variant="primary"
                            size="small"
                            className="w-full sm:w-auto"
                          />
                          <Button
                            variant="outline"
                            size="small"
                            onClick={() => handleEndSession(session.id)}
                            className="w-full sm:w-auto"
                          >
                            End session
                          </Button>
                        </>
                      ) : (
                        <Button
                          variant="outline"
                          size="small"
                          onClick={() => setSessionForCancelDelete(session)}
                          className="w-full sm:w-auto"
                        >
                          Cancel / Delete
                        </Button>
                      )
                    }
                  />
                );
              })}
            </div>
          ) : (
            <div className="text-xs text-text-secondary text-center py-2">
              No passed sessions for {selectedClub.name}
            </div>
          )}
        </div>
      )}

      {/* Quick Start Session Button */}
      {selectedClub && (
        <div className="border-t border-border pt-4 mt-4">
          <div className="text-center">
            <Button
              onClick={() => setShowStartSessionModal(true)}
              variant="primary"
              size="large"
              disabled={loading || !isAdmin}
              className="w-full"
            >
              🚀 Start New Session
            </Button>
            {!isAdmin && (
              <Text className="text-xs text-text-secondary mt-2">
                Only club administrators can start new sessions
              </Text>
            )}
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="mt-4 p-3 bg-danger-light border border-danger rounded-lg">
          <Text className="text-sm text-danger-dark">{error}</Text>
        </div>
      )}
    </Card>
  );

  // Session selection page - no Dashboard rendering
  return (
    <div className="container mx-auto p-3 sm:p-4 max-w-app-shell min-w-0">
      {/* Session Selection Interface */}
      <SessionSelectionInterface />

      {/* Start Session Modal */}
      {showStartSessionModal && (
        <CustomSessionConfigModal
          isOpen={showStartSessionModal}
          onClose={handleCloseStartSessionModal}
          onStartSession={handleSessionCreation}
          selectedClub={selectedClub}
          currentUser={currentUser}
        />
      )}

      {/* Cancel / Delete session modal (reused from Session Management) */}
      <CancelDeleteModal
        isOpen={!!sessionForCancelDelete}
        onClose={() => setSessionForCancelDelete(null)}
        onCancel={() => sessionForCancelDelete && handleCancelOrDeleteSession(sessionForCancelDelete.id, 'cancel')}
        onDelete={() => sessionForCancelDelete && handleCancelOrDeleteSession(sessionForCancelDelete.id, 'delete')}
        sessionName={sessionForCancelDelete?.name || 'This session'}
      />
    </div>
  );
}

// Main Session component - handles URL params and permissions
const Session = ({ currentUser }) => {
  const { clubId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get navigation context from state
  const fromMyClubs = location.state?.fromMyClubs;
  const fromHomepage = location.state?.fromHomepage;
  const startSessionJourney = location.state?.startSessionJourney;

  // Use club permissions hook to get club context from URL
  const { 
    club: selectedClub, 
    role: clubRole, 
    loading: clubLoading, 
    hasPermission, 
    error: clubError 
  } = useClubPermissions(clubId, currentUser);

  // Show loading state while fetching club data
  if (clubLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <Heading level={4} className="mb-4 text-text-primary">Loading...</Heading>
          <Typography variant="muted">Setting up session management</Typography>
        </div>
      </div>
    );
  }

  // Show error state if club not found or permission denied
  if (clubError || !hasPermission) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <Heading level={4} className="mb-4 text-text-primary">Access Denied</Heading>
          <Typography variant="muted" className="mb-4">
            {clubError || 'You do not have permission to start sessions for this club.'}
          </Typography>
          <BackButton
            onClick={() => navigate('/my-clubs')}
          >
            Back to My Clubs
          </BackButton>
        </div>
      </div>
    );
  }

  // Show error if no user
  if (!currentUser) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <Heading level={4} className="mb-4 text-text-primary">Authentication Required</Heading>
          <Typography variant="muted" className="mb-4">
            Please log in to access session management.
          </Typography>
          <Button
            onClick={() => navigate('/')}
            variant="primary"
          >
            Go to Home
          </Button>
        </div>
      </div>
    );
  }

  // Show error if no club found
  if (!selectedClub) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <Heading level={4} className="mb-4 text-text-primary">Club Not Found</Heading>
          <Typography variant="muted" className="mb-4">
            The requested club could not be found.
          </Typography>
          <BackButton
            onClick={() => navigate('/my-clubs')}
          >
            Back to My Clubs
          </BackButton>
        </div>
      </div>
    );
  }

  // Show the main session content wrapped with SeasonProvider
  return (
    <SeasonProvider clubId={selectedClub.id}>
      <div className="min-h-screen bg-background">
        {/* Back Button */}
        <div className="container mx-auto px-3 sm:px-6 py-4 sm:py-8 max-w-app-shell min-w-0">
          <div className="mb-6 sm:mb-8">
            <BackButton
              onClick={() => {
                navigateBack(navigate, location, clubId, `/club-management/${clubId}`);
              }}
            >
              Back to Management
            </BackButton>
          </div>

          {/* Page Title */}
          <div className="mb-6 sm:mb-8">
            <Heading level={1} className="text-xl sm:text-2xl font-bold text-text-primary break-words">
              Tonight&apos;s sessions — {selectedClub.name}
            </Heading>
          </div>

          {/* Main Content */}
          <SessionContent 
            currentUser={currentUser}
            selectedClub={selectedClub}
            clubRole={clubRole}
          />
        </div>
      </div>
    </SeasonProvider>
  );
};

export default Session;